import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { directory } from '@/lib/directory';
import { useCountry } from '@/contexts/CountryContext';
import { BusinessShowcaseCard } from '@/components/BusinessShowcaseCard';

const FILTERS = [
  { id: 'week',   label: 'Trending This Week' },
  { id: 'month',  label: 'Trending This Month' },
  { id: 'saved',  label: 'Most Saved' },
  { id: 'viewed', label: 'Most Viewed' },
];

const trendLabelFor = (id) => (
  id === 'month' ? 'Hot this month' : id === 'saved' ? 'Most saved' : id === 'viewed' ? 'Most viewed' : 'Trending'
);

// Trending Now — real businesses ranked by real weekly/monthly engagement.
// Auto-scrolling infinite marquee; pauses on hover/touch.
export const TrendingNow = () => {
  const { country } = useCountry();
  const [filter, setFilter] = useState('week');
  const [items, setItems] = useState(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let alive = true;
    setItems(null);
    directory.trending(filter, 14)
      .then((d) => { if (alive) setItems(d.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [filter]);

  if (items && items.length === 0) return null;

  const looped = items ? [...items, ...items] : [];

  return (
    <section className="py-16 bg-white overflow-hidden" data-testid="trending-now">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#D32323] mb-1">
              <Flame className="w-3.5 h-3.5" /> Live
            </div>
            <h2 className="font-heading text-3xl font-bold text-gray-900" data-testid="trending-title">
              Trending Now in {country.name}
            </h2>
            <p className="text-gray-600 mt-1 text-sm">What other travelers are saving this week</p>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0 self-start sm:self-end">
            <Link to="/search?sort=trending" data-testid="trending-view-all">View all <ArrowRight className="w-3 h-3" /></Link>
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Trending filters">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} onClick={() => setFilter(f.id)}
              className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                filter === f.id ? 'bg-[#D32323] text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid={`trending-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!items ? (
        <div className="mx-auto max-w-7xl px-4 flex gap-6">
          {[0, 1, 2, 3].map((i) => <div key={i} className="w-72 sm:w-80 h-64 rounded-2xl bg-gray-100 animate-pulse shrink-0" />)}
        </div>
      ) : (
        <div className="relative w-full"
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}
          data-testid="trending-marquee">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-20 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-20 bg-gradient-to-l from-white to-transparent z-10" />
          <div className={`flex gap-4 sm:gap-6 w-max pl-4 sm:pl-8 trending-track ${paused ? 'trending-track-paused' : ''}`}
            style={{ animationDuration: `${Math.max(items.length * 6, 30)}s` }}>
            {looped.map((b, i) => (
              <BusinessShowcaseCard key={`${b.id}-${i}`} b={b} trendLabel={trendLabelFor(filter)} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
